// Contexto de tiempo real vía SSE (EventSource nativo) + PostgreSQL LISTEN/NOTIFY.
// Reemplaza Socket.IO/polling. Funciona con múltiples instancias de Cloud Run sin Redis.
// El servidor envía: data: {"event":"task_updated","data":{...}}\n\n

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_CONFIG } from '../config/api';
import { validateToken } from '../utils/uiHandlers';

/** Rutas sin sesión de app o anónimas: no abrir SSE (evita 401 en bucle en /login). */
function isSseHoldPath(pathname) {
  if (pathname === '/') return true;
  if (pathname.startsWith('/public/survey')) return true;
  return false;
}

const RealtimeSocketContext = createContext({
  isConnected: false,
  socketEpoch: 0,
  on: () => {},
  off: () => {},
  socketRef: { current: null },
});

export function RealtimeSocketProvider({ children }) {
  const location = useLocation();
  const esRef = useRef(null);
  const listenersRef = useRef({});
  const retryTimerRef = useRef(null);
  const sseFailuresRef = useRef(0);
  const sseStoppedRef = useRef(false);
  /** Tras POST /login: permitir una conexión SSE aunque el pathname siga siendo `/` un instante. */
  const allowSseOnHoldPathRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);
  const [socketEpoch, setSocketEpoch] = useState(0);

  const connect = useCallback(() => {
    if (esRef.current) return;
    if (sseStoppedRef.current) return;

    if (typeof window !== 'undefined') {
      const path = window.location.pathname || '';
      if (isSseHoldPath(path) && !allowSseOnHoldPathRef.current) return;
    }
    allowSseOnHoldPathRef.current = false;

    const baseUrl = (API_CONFIG.BASE_URL || '').replace(/\/$/, '');
    const url = baseUrl ? `${baseUrl}/api/sse/events` : '/api/sse/events';

    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => {
      sseFailuresRef.current = 0;
      sseStoppedRef.current = false;
      setIsConnected(true);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      const { event, data } = msg || {};
      if (!event) return;
      const handlers = listenersRef.current[event];
      if (handlers) {
        handlers.forEach((cb) => {
          try { cb(data); } catch (err) {
            console.error(`Realtime handler error [${event}]:`, err);
          }
        });
      }
    };

    es.onerror = () => {
      setIsConnected(false);
      es.close();
      esRef.current = null;
      setSocketEpoch((n) => n + 1);

      sseFailuresRef.current += 1;
      const n = sseFailuresRef.current;
      if (n >= 12) {
        sseStoppedRef.current = true;
        return;
      }
      const delayMs = Math.min(60000, 3000 * 2 ** Math.min(n - 1, 4));
      retryTimerRef.current = setTimeout(async () => {
        retryTimerRef.current = null;
        if (typeof window !== 'undefined' && isSseHoldPath(window.location.pathname)) return;
        const ok = await validateToken();
        if (!ok) {
          sseStoppedRef.current = true;
          return;
        }
        connect();
      }, delayMs);
    };

    esRef.current = es;
    setSocketEpoch((n) => n + 1);
  }, []);

  useEffect(() => {
    const onTokenSet = () => {
      allowSseOnHoldPathRef.current = true;
      sseStoppedRef.current = false;
      sseFailuresRef.current = 0;
      if (!esRef.current) connect();
    };
    window.addEventListener('realtime:token_set', onTokenSet);
    return () => window.removeEventListener('realtime:token_set', onTokenSet);
  }, [connect]);

  useEffect(() => {
    if (isSseHoldPath(location.pathname)) {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setIsConnected(false);
      sseFailuresRef.current = 0;
      sseStoppedRef.current = false;
      return;
    }

    if (esRef.current) {
      sseStoppedRef.current = false;
      return;
    }

    sseStoppedRef.current = false;
    let cancelled = false;
    let bootTimer = null;
    (async () => {
      const ok = await validateToken();
      if (cancelled || !ok) return;
      bootTimer = setTimeout(() => {
        bootTimer = null;
        if (!cancelled) connect();
      }, 200);
    })();

    return () => {
      cancelled = true;
      if (bootTimer) clearTimeout(bootTimer);
    };
  }, [connect, location.pathname]);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setIsConnected(false);
    };
  }, []);

  const on = useCallback((event, callback) => {
    if (!listenersRef.current[event]) listenersRef.current[event] = new Set();
    listenersRef.current[event].add(callback);
  }, []);

  const off = useCallback((event, callback) => {
    listenersRef.current[event]?.delete(callback);
  }, []);

  const value = { isConnected, socketEpoch, on, off, socketRef: esRef };

  return (
    <RealtimeSocketContext.Provider value={value}>
      {children}
    </RealtimeSocketContext.Provider>
  );
}

export function useRealtimeSocket() {
  const ctx = useContext(RealtimeSocketContext);
  if (!ctx) throw new Error('useRealtimeSocket must be used within RealtimeSocketProvider');
  return ctx;
}

/**
 * Suscribirse a un evento y desuscribirse al desmontar.
 * @param {number} [debounceMs] — opcional: agrupa ráfagas de eventos.
 */
export function useRealtimeOn(eventName, callback, debounceMs = 0) {
  const { on, off, socketEpoch } = useRealtimeSocket();
  const cbRef = useRef(callback);
  const debounceTimerRef = useRef(null);
  cbRef.current = callback;

  useEffect(() => {
    const invoke = (data) => {
      try { cbRef.current(data); }
      catch (e) { console.error(`Realtime handler error [${eventName}]:`, e); }
    };

    const handler =
      debounceMs > 0
        ? (data) => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
              debounceTimerRef.current = null;
              invoke(data);
            }, debounceMs);
          }
        : invoke;

    on(eventName, handler);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      off(eventName, handler);
    };
  }, [eventName, on, off, socketEpoch, debounceMs]);
}
